import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, MessageCircle, Share2, Globe,
  ChevronLeft, ChevronRight, ThumbsUp, Edit3, Trash2,
  ZoomIn, ZoomOut, Maximize2, Download, FileText, Bell, BellOff,
  MessageSquareOff, MessageSquare, Bookmark, BookmarkCheck, EyeOff, Link2,
} from 'lucide-react';
import { MenuProps, Tooltip, message } from 'antd';
import { PostDropdown } from './PostDropdown';
import { UserAvatar } from './UserAvatar';
import { CommentSection } from './CommentSection';
import { EngagementModal, type EngagementTab } from './EngagementModal';
import {
  AUDIENCE_LABEL, REACTIONS, badgeOf, canDeletePost, canEditPost, htmlToText, visibleCommentCount,
  reactionSummary, sanitizeHtml, timeAgo, type Me, type Post,
} from '../socialStore';
import { comingSoon } from '../socialApi';

interface PostCardProps {
  post: Post;
  me: Me;
  now: number;
  /** Bài vừa bị ẩn trong phiên này → hiện thẻ thu gọn có "Hoàn tác" */
  hidden?: boolean;
  /** Tra bài gốc khi hiển thị bài được chia sẻ */
  findPost: (id: string) => Post | undefined;
  onShare: (post: Post) => void;
  onEdit: (post: Post) => void;
  onToggleSave: (post: Post) => void;
  onToggleHide: (post: Post) => void;
  onToggleLock: (post: Post) => void;
  onDelete: (post: Post) => void;
  /** Bài hiện ≥ 50% trên màn hình trong 1 giây */
  onViewed: (id: string) => void;
  highlighted?: boolean;
}

const AUDIENCE_ICON = { organization: Globe };

export const postLink = (id: string) => `${window.location.origin}/social?post=${encodeURIComponent(id)}`;

const copy = (text: string, ok: string) =>
  navigator.clipboard?.writeText(text).then(() => message.success(ok), () => message.error('Trình duyệt không cho phép sao chép'));

function PostHeader({ post, now, small }: { post: Post; now: number; small?: boolean }) {
  return (
    <div className="flex items-center space-x-3 min-w-0">
      <UserAvatar name={post.authorName} src={post.authorAvatar} size={small ? 32 : 40} />
      <div className="min-w-0">
        <div className="flex items-center text-sm flex-wrap gap-1">
          <span className="font-bold text-gray-900 dark:text-gray-100">{post.authorName}</span>
          {post.organization && (
            <>
              <span className="text-gray-400 dark:text-gray-600 text-xs">▸</span>
              <span className="font-bold text-gray-900 dark:text-gray-100">{post.organization}</span>
            </>
          )}
        </div>
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-0.5 gap-1.5">
          <span title={new Date(post.publishAt).toLocaleString('vi-VN')}>{timeAgo(post.publishAt, now)}</span>
          {post.editedAt && <span>· đã chỉnh sửa</span>}
          <span>·</span>
          {post.audiences.map(a => {
            const Icon = AUDIENCE_ICON[a] || Globe;
            return <Tooltip key={a} title={AUDIENCE_LABEL[a]}><Icon className="w-3 h-3" /></Tooltip>;
          })}
        </div>
      </div>
    </div>
  );
}

/** Nội dung HTML đã lọc; bài dài thu gọn và có "Xem thêm" */
function PostBody({ post, clamp = true }: { post: Post; clamp?: boolean }) {
  const html = useMemo(() => sanitizeHtml(post.html), [post.html]);
  const long = useMemo(() => htmlToText(post.html).length > 500, [post.html]);
  const [open, setOpen] = useState(!clamp);
  return (
    <div>
      {post.title && <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{post.title}</h2>}
      <div className={`relative ${long && !open ? 'max-h-48 overflow-hidden' : ''}`}>
        {/* @nhắc tên: tô theo data-mention-id (bài tạo ngoài trình soạn thảo có thể thiếu class "mention") */}
        <div className="social-post-html [&_[data-mention-id]]:font-semibold [&_[data-mention-id]]:text-green-700 dark:[&_[data-mention-id]]:text-green-400 text-[14px] text-gray-800 leading-relaxed dark:text-gray-300 break-words" dangerouslySetInnerHTML={{ __html: html }} />
        {long && !open && <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-white dark:from-gray-800" />}
      </div>
      {long && <button onClick={() => setOpen(!open)} className="mt-1 text-sm font-bold text-green-600 hover:underline cursor-pointer">{open ? 'Thu gọn' : 'Xem thêm'}</button>}
    </div>
  );
}

/** Tải ảnh về máy: <a download> không có tác dụng với ảnh khác domain, kho ảnh cho phép CORS nên đọc thành blob */
async function downloadImage(url: string, name: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error();
    const href = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.click();
    URL.revokeObjectURL(href);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function ImageGrid({ images, size, onOpen }: { images: string[]; size?: { width: number | null; height: number | null }; onOpen: (i: number) => void }) {
  if (!images.length) return null;
  if (images.length === 1) {
    return (
      <div onClick={() => onOpen(0)} className="cursor-pointer hover:opacity-95 transition overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900">
        <img src={images[0]} alt="Ảnh bài viết" width={size?.width ?? undefined} height={size?.height ?? undefined} className="w-full max-h-[420px] object-contain" />
      </div>
    );
  }
  const shown = images.slice(0, images.length === 2 ? 2 : 3);
  return (
    <div className={`grid ${shown.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-1 rounded-lg overflow-hidden`}>
      {shown.map((img, idx) => (
        <div key={idx} onClick={() => onOpen(idx)} className="cursor-pointer hover:opacity-95 transition h-[200px] md:h-[240px] relative">
          <img src={img} alt={`Ảnh ${idx + 1}`} className="w-full h-full object-cover" />
          {idx === shown.length - 1 && images.length > shown.length && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold text-xl">+{images.length - shown.length}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function RecognitionBanner({ post }: { post: Post }) {
  const r = post.recognition;
  if (!r) return null;
  const b = badgeOf(r.badge);
  return (
    <div className="mb-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-3 flex items-center gap-3">
      <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center text-2xl">{b.emoji}</div>
      <div className="min-w-0">
        <div className="text-sm text-gray-800 dark:text-gray-200">
          <span className="font-bold">{post.authorName}</span> đã ghi nhận{' '}
          {r.people.map((p, i) => (
            <React.Fragment key={String(p.id)}>
              {i > 0 && (i === r.people.length - 1 ? ' và ' : ', ')}
              <span className="font-bold">{p.name}</span>
            </React.Fragment>
          ))}
        </div>
        <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-0.5">Huy hiệu: {b.label}</div>
      </div>
    </div>
  );
}

function SharedPost({ original, now }: { original?: Post; now: number }) {
  if (!original) {
    return <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-500">Bài viết gốc đã bị xóa hoặc không còn hiển thị.</div>;
  }
  const text = htmlToText(original.html);
  return (
    <a href={`/social?post=${original.id}`} className="block rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
      <PostHeader post={original} now={now} small />
      {original.title && <div className="font-bold text-gray-900 dark:text-gray-100 mt-2">{original.title}</div>}
      {original.kind === 'recognition' && original.recognition && (
        <div className="text-sm mt-2">{badgeOf(original.recognition.badge).emoji} Ghi nhận {original.recognition.people.map(p => p.name).join(', ')}</div>
      )}
      {text && <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 line-clamp-3">{text}</p>}
      {original.images[0] && <img src={original.images[0]} alt="" className="mt-2 rounded-md max-h-48 w-full object-cover" />}
    </a>
  );
}

function ReactionBar({ post, me, onComment, onShare }: { post: Post; me: Me; onComment: () => void; onShare: () => void }) {
  const { mine } = reactionSummary(post, me.id);
  const mineMeta = REACTIONS.find(r => r.emoji === mine);
  // Máy tính: rê chuột để chọn cảm xúc. Điện thoại: nhấn giữ nút Thích. Bàn phím: Tab tới nút Thích.
  const [pickerOpen, setPickerOpen] = useState(false);
  const holdTimer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e: Event) => { if (!wrapRef.current?.contains(e.target as Node)) setPickerOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [pickerOpen]);

  const startHold = () => {
    longPressed.current = false;
    holdTimer.current = window.setTimeout(() => { longPressed.current = true; setPickerOpen(true); }, 450);
  };
  const endHold = () => { if (holdTimer.current) window.clearTimeout(holdTimer.current); };
  // API cảm xúc: Đợt 5
  const pick = (_emoji: string | null) => { comingSoon('Thả cảm xúc'); setPickerOpen(false); };

  return (
    <div className="flex items-center justify-between py-1 relative">
      <div ref={wrapRef} className="flex-1 relative group">
        <div className={`absolute bottom-full left-0 mb-1 pb-1 transition-all duration-200 z-30 ${pickerOpen
          ? 'opacity-100 visible translate-y-0'
          : 'opacity-0 invisible translate-y-3 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:visible group-has-[:focus-visible]:translate-y-0'}`}>
          <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 shadow-lg rounded-full px-3 py-2 flex items-center space-x-1.5">
            {REACTIONS.map(item => (
              <Tooltip key={item.emoji} title={item.label}>
                <button
                  aria-label={item.label}
                  onClick={() => pick(mine === item.emoji ? null : item.emoji)}
                  className={`hover:scale-125 transition-transform text-2xl hover:-translate-y-1 cursor-pointer ${mine === item.emoji ? 'scale-110' : ''}`}
                >
                  {item.emoji}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
        <button
          onTouchStart={startHold}
          onTouchEnd={endHold}
          onTouchMove={endHold}
          onContextMenu={e => { if (longPressed.current) e.preventDefault(); }}
          onClick={() => {
            // nhấn giữ đã mở bảng cảm xúc thì không tính là bấm Thích
            if (longPressed.current) { longPressed.current = false; return; }
            pick(mine ? null : '👍');
          }}
          className={`w-full flex items-center justify-center space-x-2 py-2 rounded-lg font-bold text-sm transition hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer select-none ${mine ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}`}
        >
          {mine && mine !== '👍' ? <span className="text-lg leading-none">{mine}</span> : <ThumbsUp className={`w-5 h-5 ${mine ? 'fill-current' : ''}`} />}
          <span>{mineMeta ? mineMeta.label : 'Thích'}</span>
        </button>
      </div>
      <button onClick={onComment} className="flex-1 flex items-center justify-center space-x-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 py-2 rounded-lg font-bold text-sm transition cursor-pointer">
        <MessageCircle className="w-5 h-5" />
        <span>Bình luận</span>
      </button>
      <button onClick={onShare} className="flex-1 flex items-center justify-center space-x-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 py-2 rounded-lg font-bold text-sm transition cursor-pointer">
        <Share2 className="w-5 h-5" />
        <span>Chia sẻ</span>
      </button>
    </div>
  );
}

function Stats({ post, me }: { post: Post; me: Me }) {
  const { total, top } = reactionSummary(post, me.id);
  const cmt = visibleCommentCount(post, me);
  const [tab, setTab] = useState<EngagementTab | null>(null);
  return (
    <div className="flex justify-between items-center text-gray-500 dark:text-gray-400 text-xs py-1.5 font-medium gap-2">
      <div className="flex items-center gap-1 min-w-0">
        {total > 0 && (
          <Tooltip title={<>{top.map(t => `${t.emoji} ${t.count}`).join('  ')}<div className="opacity-70">Bấm để xem ai đã thả cảm xúc</div></>}>
            <button type="button" onClick={() => setTab('all')} className="flex items-center gap-1 hover:underline cursor-pointer">
              <span className="flex -space-x-1">{top.slice(0, 3).map(t => <span key={t.emoji} className="text-sm">{t.emoji}</span>)}</span>
              <span>{total}</span>
            </button>
          </Tooltip>
        )}
      </div>
      <span className="whitespace-nowrap">
        {cmt > 0 && <span>{cmt} bình luận · </span>}
        <button type="button" onClick={() => setTab('views')} className="hover:underline cursor-pointer">{post.views} lượt xem</button>
      </span>
      {tab && <EngagementModal post={post} me={me} open initialTab={tab} onClose={() => setTab(null)} />}
    </div>
  );
}

export function PostCard({ post, me, now, hidden, findPost, onShare, onEdit, onToggleSave, onToggleHide, onToggleLock, onDelete, onViewed, highlighted }: PostCardProps) {
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const saved = !!post.viewer?.saved;
  const muted = !!post.viewer?.muted;

  // Ghi lượt xem khi bài thực sự hiện trên màn hình: ≥ 50% bài (hoặc nửa màn hình với bài dài) trong 1 giây
  useEffect(() => {
    const el = cardRef.current;
    if (!el || hidden || post.scheduled || typeof IntersectionObserver === 'undefined') return;
    let timer: number | undefined;
    const io = new IntersectionObserver(([en]) => {
      window.clearTimeout(timer);
      const seen = en.isIntersecting && (en.intersectionRatio >= 0.5 || en.intersectionRect.height >= window.innerHeight / 2);
      if (!seen) return;
      timer = window.setTimeout(() => { io.disconnect(); onViewed(post.id); }, 1000);
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    io.observe(el);
    return () => { window.clearTimeout(timer); io.disconnect(); };
  }, [post.id, post.scheduled, hidden]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (highlighted) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [highlighted]);

  if (hidden) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300"><EyeOff className="w-4 h-4" /> Đã ẩn bài viết khỏi bảng tin của bạn.</div>
        <button onClick={() => onToggleHide(post)} className="font-bold text-green-600 hover:underline cursor-pointer">Hoàn tác</button>
      </div>
    );
  }

  const isAuthor = canEditPost(post, me);
  // Tắt thông báo: hoãn — giữ mục menu, bấm báo "sắp có"
  const items: MenuProps['items'] = [
    { key: 'link', label: 'Sao chép link bài viết', icon: <Link2 className="w-4 h-4 text-gray-500" />, onClick: () => copy(postLink(post.id), 'Đã sao chép link bài viết') },
    { key: 'text', label: 'Sao chép nội dung', icon: <FileText className="w-4 h-4 text-gray-500" />, onClick: () => copy([post.title, htmlToText(post.html)].filter(Boolean).join('\n\n'), 'Đã sao chép nội dung') },
    { key: 'save', label: saved ? 'Bỏ lưu bài viết' : 'Lưu bài viết', icon: saved ? <BookmarkCheck className="w-4 h-4 text-green-600" /> : <Bookmark className="w-4 h-4 text-gray-500" />, onClick: () => onToggleSave(post) },
    { key: 'mute', label: muted ? 'Bật thông báo về bài viết này' : 'Tắt thông báo về bài viết này', icon: muted ? <Bell className="w-4 h-4 text-gray-500" /> : <BellOff className="w-4 h-4 text-gray-500" />, onClick: () => comingSoon('Tắt thông báo bài viết') },
    ...(canDeletePost(post, me) ? [{
      key: 'lock', label: post.commentsLocked ? 'Mở lại bình luận' : 'Chặn và ẩn bình luận',
      icon: post.commentsLocked ? <MessageSquare className="w-4 h-4 text-gray-500" /> : <MessageSquareOff className="w-4 h-4 text-gray-500" />,
      onClick: () => onToggleLock(post),
    }] : []),
    ...(isAuthor ? [{ key: 'edit', label: 'Chỉnh sửa bài viết', icon: <Edit3 className="w-4 h-4 text-gray-500" />, onClick: () => onEdit(post) }] : []),
    { type: 'divider' as const },
    ...(!isAuthor ? [{ key: 'hide', label: 'Ẩn bài viết', icon: <EyeOff className="w-4 h-4 text-gray-500" />, onClick: () => onToggleHide(post) }] : []),
    ...(canDeletePost(post, me) ? [{
      key: 'delete', danger: true, label: 'Xóa bài viết', icon: <Trash2 className="w-4 h-4" />, onClick: () => onDelete(post),
    }] : []),
  ];

  const focusComment = () => {
    if (post.commentsLocked) { message.info('Bình luận đã bị tắt cho bài viết này'); return; }
    commentRef.current?.focus();
  };

  return (
    <>
      <div ref={cardRef} id={`post-${post.id}`} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border overflow-hidden scroll-mt-4 transition ${highlighted ? 'border-green-500 ring-2 ring-green-500/30' : 'border-gray-200 dark:border-gray-700'}`}>
        <div className="p-3 md:p-4 flex justify-between items-start">
          <PostHeader post={post} now={now} />
          <div className="flex items-center gap-1">
            {saved && <Tooltip title="Đã lưu"><BookmarkCheck className="w-4 h-4 text-green-600" /></Tooltip>}
            <PostDropdown items={items} />
          </div>
        </div>

        <div className="px-3 md:px-4 pb-3">
          {post.kind === 'recognition' && <RecognitionBanner post={post} />}
          <PostBody post={post} />
          {post.sharedFrom && <div className="mt-3"><SharedPost original={findPost(post.sharedFrom)} now={now} /></div>}
        </div>

        {post.images.length > 0 && <div className="px-3 pb-3"><ImageGrid images={post.images} size={post.imageMeta?.[0]} onOpen={setLightbox} /></div>}

        <div className="px-3 md:px-4 pb-3">
          <Stats post={post} me={me} />
          <div className="border-t border-gray-200 dark:border-gray-700" />
          <ReactionBar post={post} me={me} onComment={focusComment} onShare={() => onShare(post)} />
          <div className="border-t border-gray-200 dark:border-gray-700 mb-3" />
          <CommentSection post={post} me={me} now={now} inputRef={commentRef} />
        </div>
      </div>

      {lightbox !== null && createPortal(
        <Lightbox post={post} me={me} now={now} index={lightbox} setIndex={setLightbox} onShare={() => onShare(post)} />,
        document.body,
      )}
    </>
  );
}

/** Xem ảnh toàn màn hình, bên phải là nội dung và bình luận */
function Lightbox({ post, me, now, index, setIndex, onShare }: { post: Post; me: Me; now: number; index: number; setIndex: (i: number | null) => void; onShare: () => void }) {
  const [scale, setScale] = useState(1);
  const images = post.images;
  const n = images.length;
  const go = (d: number) => { setScale(1); setIndex((index + d + n) % n); };
  const close = () => {
    setIndex(null);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight' && n > 1) go(1);
      if (e.key === 'ArrowLeft' && n > 1) go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="fixed inset-0 z-[1000] bg-black/95 flex flex-col md:flex-row overflow-hidden">
      <div className="absolute top-4 left-4 z-50">
        <button onClick={close} title="Đóng (Esc)" className="w-10 h-10 rounded-full bg-gray-800/80 hover:bg-gray-700 text-white flex items-center justify-center cursor-pointer shadow-lg"><X className="w-6 h-6" /></button>
      </div>
      <div className="absolute top-4 right-4 md:right-[440px] lg:right-[500px] z-50 flex items-center space-x-2 bg-gray-900/60 backdrop-blur-md px-3 py-1.5 rounded-full text-white">
        <button onClick={() => setScale(s => Math.min(s + 0.25, 3))} className="p-1.5 hover:bg-white/10 rounded-full cursor-pointer" title="Phóng to"><ZoomIn className="w-4 h-4" /></button>
        <button onClick={() => setScale(s => Math.max(s - 0.25, 1))} className="p-1.5 hover:bg-white/10 rounded-full cursor-pointer" title="Thu nhỏ"><ZoomOut className="w-4 h-4" /></button>
        <button onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {})} className="p-1.5 hover:bg-white/10 rounded-full cursor-pointer" title="Toàn màn hình"><Maximize2 className="w-4 h-4" /></button>
        <button onClick={() => downloadImage(images[index], `anh-${index + 1}.jpg`)} className="p-1.5 hover:bg-white/10 rounded-full cursor-pointer" title="Tải ảnh"><Download className="w-4 h-4" /></button>
      </div>

      <div className="flex-1 h-[55vh] md:h-full flex items-center justify-center relative bg-black select-none">
        {n > 1 && <button onClick={() => go(-1)} className="absolute left-4 z-20 w-12 h-12 rounded-full bg-gray-800/70 hover:bg-gray-700 text-white flex items-center justify-center cursor-pointer"><ChevronLeft className="w-7 h-7" /></button>}
        <div className="p-4 flex items-center justify-center w-full h-full overflow-auto">
          <img src={images[index]} alt={`Ảnh ${index + 1}`} style={{ transform: `scale(${scale})`, transition: 'transform 0.2s ease-in-out' }} className="max-h-[85vh] max-w-full object-contain rounded" />
        </div>
        {n > 1 && <button onClick={() => go(1)} className="absolute right-4 z-20 w-12 h-12 rounded-full bg-gray-800/70 hover:bg-gray-700 text-white flex items-center justify-center cursor-pointer"><ChevronRight className="w-7 h-7" /></button>}
        {n > 1 && <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-xs font-semibold">{index + 1} / {n}</div>}
      </div>

      <div className="w-full md:w-[420px] lg:w-[480px] h-[45vh] md:h-full bg-white dark:bg-gray-900 flex flex-col border-t md:border-t-0 md:border-l border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0"><PostHeader post={post} now={now} /></div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <PostBody post={post} clamp={false} />
          <Stats post={post} me={me} />
          <div className="border-y border-gray-200 dark:border-gray-800">
            <ReactionBar post={post} me={me} onComment={() => document.getElementById(`lb-cmt-${post.id}`)?.querySelector('textarea')?.focus()} onShare={onShare} />
          </div>
          <div id={`lb-cmt-${post.id}`}><CommentSection post={post} me={me} now={now} collapsed={false} /></div>
        </div>
      </div>
    </div>
  );
}
